import React, { useEffect, useRef } from "react";
import { pickColor, rgba } from "./internal/colorUtils";

export const key = "liquid-blobs";

const DEFAULT_BG = "#0c0a1f";
const DEFAULT_BLOB_A = "#a855f7";
const DEFAULT_BLOB_B = "#ec4899";
const DEFAULT_BLOB_C = "#22d3ee";

// True metaballs need a marching-squares or shader pass; a much cheaper
// approximation that still reads as "liquid" is to render each blob as a
// radial-gradient disk with hard alpha falloff and use the "lighter"
// composite mode. Where two blobs overlap, their gradients add together,
// producing the same bulging-membrane look without per-pixel work.
const BLOB_COUNT = 9;
const BLOB_MIN_RADIUS_FRAC = 0.18;
const BLOB_MAX_RADIUS_FRAC = 0.34;
const SPEED = 0.28;
const BG_FADE_ALPHA = 1; // we repaint the bg fully each frame (no trails)
const BLUR_PX = 28;

function rand(min, max) {
  return min + Math.random() * (max - min);
}

export default function LiquidBlobs({ colors = [], style }) {
  const canvasRef = useRef(null);
  const rafRef = useRef(null);
  const blobsRef = useRef([]);
  const colorsRef = useRef([DEFAULT_BG, DEFAULT_BLOB_A, DEFAULT_BLOB_B, DEFAULT_BLOB_C]);

  useEffect(() => {
    colorsRef.current = [
      pickColor(colors, 0, DEFAULT_BG),
      pickColor(colors, 1, DEFAULT_BLOB_A),
      pickColor(colors, 2, DEFAULT_BLOB_B),
      pickColor(colors, 3, DEFAULT_BLOB_C),
    ];
  }, [colors]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    const ctx = canvas.getContext("2d");
    if (!ctx) return undefined;

    let width = 0;
    let height = 0;
    const dpr = typeof window !== "undefined" ? Math.min(window.devicePixelRatio || 1, 2) : 1;

    const seedBlobs = () => {
      const minDim = Math.min(width, height);
      const arr = [];
      for (let i = 0; i < BLOB_COUNT; i += 1) {
        arr.push({
          x: rand(0, width),
          y: rand(0, height),
          vx: rand(-SPEED, SPEED),
          vy: rand(-SPEED, SPEED),
          radius: rand(minDim * BLOB_MIN_RADIUS_FRAC, minDim * BLOB_MAX_RADIUS_FRAC),
          slot: 1 + (i % 3),
          phase: Math.random() * Math.PI * 2,
        });
      }
      blobsRef.current = arr;
    };

    const resize = () => {
      const parent = canvas.parentElement;
      if (!parent) return;
      // Use clientWidth/Height (layout dims) so an ancestor `transform: scale(...)`
      // (e.g. PreviewDeviceStage) doesn't shrink the canvas and pin it to top-left.
      width = Math.max(1, parent.clientWidth || parent.offsetWidth || 0);
      height = Math.max(1, parent.clientHeight || parent.offsetHeight || 0);
      canvas.width = Math.floor(width * dpr);
      canvas.height = Math.floor(height * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      seedBlobs();
    };

    let t = 0;

    const tick = () => {
      const cArr = colorsRef.current;
      const bg = cArr[0];

      ctx.globalAlpha = BG_FADE_ALPHA;
      ctx.fillStyle = bg;
      ctx.fillRect(0, 0, width, height);

      const blobs = blobsRef.current;

      // Update positions — bouncing off the walls with a cushioned bounce so
      // they don't all visibly hit at once.
      for (let i = 0; i < blobs.length; i += 1) {
        const b = blobs[i];
        // Add a tiny breathing offset so even idle blobs feel alive.
        const breathe = Math.sin(t * 0.7 + b.phase) * 0.12;
        b.x += b.vx * (1 + breathe);
        b.y += b.vy * (1 + breathe);
        if (b.x < b.radius * 0.4) { b.x = b.radius * 0.4; b.vx = Math.abs(b.vx); }
        if (b.x > width - b.radius * 0.4) { b.x = width - b.radius * 0.4; b.vx = -Math.abs(b.vx); }
        if (b.y < b.radius * 0.4) { b.y = b.radius * 0.4; b.vy = Math.abs(b.vy); }
        if (b.y > height - b.radius * 0.4) { b.y = height - b.radius * 0.4; b.vy = -Math.abs(b.vy); }
      }

      const supportsFilter = "filter" in ctx;
      if (supportsFilter) ctx.filter = `blur(${BLUR_PX}px)`;
      ctx.globalCompositeOperation = "lighter";

      for (let i = 0; i < blobs.length; i += 1) {
        const b = blobs[i];
        const color = cArr[b.slot] || cArr[1];
        const grad = ctx.createRadialGradient(b.x, b.y, b.radius * 0.05, b.x, b.y, b.radius);
        grad.addColorStop(0, rgba(color, 0.95));
        grad.addColorStop(0.5, rgba(color, 0.45));
        grad.addColorStop(1, rgba(color, 0));
        ctx.fillStyle = grad;
        ctx.beginPath();
        ctx.arc(b.x, b.y, b.radius, 0, Math.PI * 2);
        ctx.fill();
      }

      ctx.globalCompositeOperation = "source-over";
      if (supportsFilter) ctx.filter = "none";

      t += 0.016;
      rafRef.current = requestAnimationFrame(tick);
    };

    resize();
    rafRef.current = requestAnimationFrame(tick);

    let ro = null;
    if (typeof ResizeObserver !== "undefined" && canvas.parentElement) {
      ro = new ResizeObserver(resize);
      ro.observe(canvas.parentElement);
    } else if (typeof window !== "undefined") {
      window.addEventListener("resize", resize);
    }

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      if (ro) {
        try { ro.disconnect(); } catch { /* noop */ }
      } else if (typeof window !== "undefined") {
        window.removeEventListener("resize", resize);
      }
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      style={{
        position: "absolute",
        inset: 0,
        width: "100%",
        height: "100%",
        display: "block",
        pointerEvents: "none",
        ...style,
      }}
    />
  );
}
